import React, {useCallback} from 'react';

import {BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {AccountLicenseLink, AccountLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {useLicenseTypes} from 'src/hooks/dictionaries';
import {
    getEnabledLicensesModulesProgress,
    getEnabledLicensesModulesResult,
} from 'src/openapi/admin/apis/BulkLicensesModulesApi';
import {useAppDispatch} from 'src/store/hooks';

import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import {ResultModalDescription} from '../result-modal-description';

import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {Features, GetResultSuccess} from '../typedef';
import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {UpdateLicenseResultItem} from 'src/openapi/admin/models/UpdateLicenseResultItem';


type Props = {
    id: string,
    onClose: () => void,
};

export const LicenseAccessModuleEnableResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();
    const {getLicenseTypeByCode} = useLicenseTypes();

    const getPaginatedResults = useCallback(
        (bulkResult: UpdateLicenseResultItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: UpdateLicenseResultItem, index) => (
                <Row key={index}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        <AccountLink
                            blank={true}
                            accountId={res.accountId || 0}
                            accountName={res.accountName || ''}
                            place="Bulk License Update Module Result"
                        />
                    </Cell>
                    <Cell>
                        <AccountLicenseLink
                            blank={true}
                            accountId={res.accountId || 0}
                            licenseId={res.licenseId || 0}
                            licenseType={getLicenseTypeByCode(res.licenseType) || res.licenseType}
                            place="Bulk License Update Module Result"
                        />
                    </Cell>
                    <Cell>
                        <CompleteMark errors={res.errors}/>
                    </Cell>
                </Row>
            )),
        [getLicenseTypeByCode],
    );

    return (
        <BulkActionResultModal<UpdateLicenseResultItem, Features>
            id={id}
            onClose={onClose}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <ResultModalDescription
                    toolTitle={TOOL_TITLE}
                    fileName={FILE_NAME}
                    fileHeaders={FILE_HEADERS}
                    bulkResult={bulkResult}
                />
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={50}>
                            <RowNumbered
                                maxRowNumber={bulkResult?.length || 0}
                                placeholder={<strong>#</strong>}
                            />
                        </Cell>
                        <Cell width={246}>
                            <strong>Account Name ({bulkResult?.length})</strong>
                        </Cell>
                        <Cell width={200}>License Type</Cell>
                        <Cell width={150}>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getEnabledLicensesModulesProgress({id}))}
            getResult={async () => {
                const response = await apiDispatch(getEnabledLicensesModulesResult({id}));

                if (response.payload.success) {
                    const payload: GetResultSuccess = {
                        success: true,
                        error: false,
                        data: {
                            itemList: response.payload.data.result,
                            startTime: response.payload.data.startTime,
                            additionalData: {
                                dataModules: response.payload.data.dataModules,
                                featureModules: response.payload.data.featureModules,
                                availableFeatures: response.payload.data.availableFeatures,
                            },
                        },
                    };

                    return Promise.resolve({type: '', payload});
                } else {
                    return Promise.resolve({type: '', payload: response.payload});
                }
            }}
            sortComparator={(a, b) => a.accountName.localeCompare(b.accountName)}
        />
    );
};
